
import { Navigate } from 'react-router-dom'
import { usePermissions, useRoleGuard } from '@/hooks/usePermissions'
import { useAuthStore } from '@/stores/authStore'
import { LoadingSpinner } from '@/components/common/LoadingScreen'
import type { User, Permission } from '@/types'

interface ProtectedRouteProps {
  children: React.ReactNode
  requiredRoles?: User['role'][]
  requiredPermissions?: Permission[]
  requireAnyPermission?: boolean
  fallback?: React.ReactNode
  redirectTo?: string
}

/**
 * Component that protects routes based on user roles and permissions
 */
export function ProtectedRoute({
  children,
  requiredRoles = [],
  requiredPermissions = [],
  requireAnyPermission = false,
  fallback,
  redirectTo = '/auth'
}: ProtectedRouteProps) {
  const { user } = usePermissions()
  const { isLoading, error } = useAuthStore()
  const { requireRole, requirePermission, requireAnyPermission: checkAnyPermission, isAuthenticated } = useRoleGuard()

  // Show loading state while checking authentication
  if (isLoading) {
    return (
      <div className="min-h-screen bg-dark-950 flex items-center justify-center">
        <div className="flex flex-col items-center space-y-4">
          <LoadingSpinner size="large" />
          <p className="text-dark-300">Checking permissions...</p>
        </div>
      </div>
    )
  }

  // Show error state if there's a critical error
  if (error && !isAuthenticated) {
    return (
      <div className="min-h-screen bg-dark-950 flex items-center justify-center">
        <div className="text-center space-y-4 max-w-md mx-auto px-6">
          <div className="w-16 h-16 mx-auto bg-red-600/20 rounded-full flex items-center justify-center">
            <svg className="w-8 h-8 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.5 0L4.268 18.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
          </div>
          <div>
            <h1 className="text-2xl font-bold text-white mb-2">Authentication Error</h1>
            <p className="text-dark-300 mb-4">{error}</p>
          </div>
          <button 
            onClick={() => window.location.reload()}
            className="bg-red-600 hover:bg-red-700 text-white font-medium py-2 px-4 rounded-lg transition-colors"
          >
            Retry
          </button>
        </div>
      </div>
    )
  }

  // Check if user is authenticated
  if (!isAuthenticated) {
    return <Navigate to={redirectTo} replace />
  }

  try {
    // Check role requirements
    if (requiredRoles.length > 0 && !requireRole(requiredRoles)) {
      if (fallback) return <>{fallback}</>
      return <Navigate to="/unauthorized" replace />
    }

    // Check permission requirements
    if (requiredPermissions.length > 0) {
      const hasRequiredPermissions = requireAnyPermission 
        ? checkAnyPermission(requiredPermissions)
        : requiredPermissions.every(permission => requirePermission(permission))

      if (!hasRequiredPermissions) {
        if (fallback) return <>{fallback}</>
        return <Navigate to="/unauthorized" replace />
      }
    }

    return <>{children}</>
  } catch (error) {

    // Fallback to unauthorized if there's any error
    if (fallback) return <>{fallback}</>
    return <Navigate to="/unauthorized" replace />
  }
}

/**
 * Component that conditionally renders content based on permissions
 */
interface ConditionalRenderProps {
  children: React.ReactNode
  requiredRoles?: User['role'][]
  requiredPermissions?: Permission[]
  requireAnyPermission?: boolean
  fallback?: React.ReactNode
  requireAuth?: boolean
}

export function ConditionalRender({
  children,
  requiredRoles = [],
  requiredPermissions = [],
  requireAnyPermission = false,
  fallback = null,
  requireAuth = true
}: ConditionalRenderProps) {
  const { user } = usePermissions()
  const { isLoading } = useAuthStore()
  const { requireRole, requirePermission, requireAnyPermission: checkAnyPermission, isAuthenticated } = useRoleGuard()

  // Show loading state if needed
  if (isLoading && requireAuth) {
    return <>{fallback}</>
  }

  try {
    // Check authentication requirement
    if (requireAuth && !isAuthenticated) {
      return <>{fallback}</>
    }

    // Check role requirements
    if (requiredRoles.length > 0 && !requireRole(requiredRoles)) {
      return <>{fallback}</>
    }

    // Check permission requirements
    if (requiredPermissions.length > 0) {
      const hasRequiredPermissions = requireAnyPermission 
        ? checkAnyPermission(requiredPermissions)
        : requiredPermissions.every(permission => requirePermission(permission))

      if (!hasRequiredPermissions) {
        return <>{fallback}</>
      }
    }

    return <>{children}</>
  } catch (error) {

    return <>{fallback}</>
  }
}

/**
 * Higher-order component for protecting components
 */
export function withPermissions<P extends object>(
  Component: React.ComponentType<P>,
  requiredPermissions: Permission[],
  fallback?: React.ReactNode
) {
  return function PermissionWrapper(props: P) {
    return (
      <ConditionalRender 
        requiredPermissions={requiredPermissions}
        fallback={fallback}
      >
        <Component {...props} />
      </ConditionalRender>
    )
  }
}

/**
 * Higher-order component for protecting components by role
 */
export function withRoles<P extends object>(
  Component: React.ComponentType<P>,
  requiredRoles: User['role'][],
  fallback?: React.ReactNode
) {
  return function RoleWrapper(props: P) {
    return (
      <ConditionalRender 
        requiredRoles={requiredRoles}
        fallback={fallback}
      >
        <Component {...props} />
      </ConditionalRender>
    )
  }
}

/**
 * Unauthorized access page component
 */
export function UnauthorizedPage() {
  return (
    <div className="min-h-screen bg-gradient-to-br from-dark-950 via-dark-900 to-dark-950 flex items-center justify-center">
      <div className="text-center space-y-6 max-w-md mx-auto px-6">
        <div className="w-24 h-24 mx-auto bg-red-600/20 rounded-full flex items-center justify-center">
          <svg className="w-12 h-12 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.5 0L4.268 18.5c-.77.833.192 2.5 1.732 2.5z" />
          </svg>
        </div>
        
        <div>
          <h1 className="text-3xl font-bold text-white mb-2">Access Denied</h1>
          <p className="text-dark-300">
            You don't have the required permissions to access this page.
          </p>
        </div>
        
        <div className="space-y-3">
          <button 
            onClick={() => window.history.back()}
            className="w-full bg-red-600 hover:bg-red-700 text-white font-medium py-2 px-4 rounded-lg transition-colors"
          >
            Go Back
          </button>
          <button 
            onClick={() => window.location.href = '/'}
            className="w-full bg-dark-700 hover:bg-dark-600 text-white font-medium py-2 px-4 rounded-lg transition-colors"
          >
            Return Home
          </button>
        </div>
      </div>
    </div>
  )
}